import { gql, useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../../graphql/hooks/useFeatureFlag";
import { useAiInterviewerContext } from "../AiInterviewerContext";
import { GuideQuestionSuggestionsBucketV2 } from "./types";

const AI_INTERVIEWER_QUESTION_SUGGESTIONS_V2_QUERY = gql`
  query AiInterviewerQuestionSuggestionsV2(
    $aiInterviewerId: ID!
    $forceRefresh: Boolean
  ) {
    aiInterviewerQuestionSuggestionsV2(
      aiInterviewerId: $aiInterviewerId
      forceRefresh: $forceRefresh
    ) {
      status
      generatedForVersionId
      errorMessage
      result {
        questionSuggestions {
          questionId
          originalQuestionId
          questionIndex
          questionText
          analysisError
          suggestions {
            id
            questionId
            questionIndex
            category
            impactScore
            title
            description
            fieldChanges {
              field
              index
              positionMode
              value
            }
          }
        }
      }
    }
  }
`;

const IGNORE_GUIDE_QUESTION_SUGGESTION_V2_MUTATION = gql`
  mutation IgnoreGuideQuestionSuggestionV2(
    $aiInterviewerId: ID!
    $suggestionId: ID!
  ) {
    ignoreGuideQuestionSuggestionV2(
      aiInterviewerId: $aiInterviewerId
      suggestionId: $suggestionId
    ) {
      success
    }
  }
`;

const APPLY_GUIDE_QUESTION_SUGGESTION_V2_MUTATION = gql`
  mutation ApplyGuideQuestionSuggestionV2(
    $aiInterviewerId: ID!
    $suggestionId: ID!
  ) {
    applyGuideQuestionSuggestionV2(
      aiInterviewerId: $aiInterviewerId
      suggestionId: $suggestionId
    ) {
      success
    }
  }
`;

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 300000;
type SuggestionsStatus = "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";

type QueryData = {
  aiInterviewerQuestionSuggestionsV2: {
    status: SuggestionsStatus;
    generatedForVersionId?: string | null;
    errorMessage?: string | null;
    result?: {
      questionSuggestions: GuideQuestionSuggestionsBucketV2[];
    } | null;
  } | null;
};

type QueryVars = {
  aiInterviewerId: string;
  forceRefresh?: boolean;
};

type IgnoreMutationData = {
  ignoreGuideQuestionSuggestionV2: {
    success: boolean;
  };
};

type IgnoreMutationVars = {
  aiInterviewerId: string;
  suggestionId: string;
};

type ApplyMutationData = {
  applyGuideQuestionSuggestionV2: {
    success: boolean;
  };
};

type ApplyMutationVars = {
  aiInterviewerId: string;
  suggestionId: string;
};

type UseGuideAssistantResult = {
  isFeatureEnabled: boolean;
  loading: boolean;
  totalError: boolean;
  hasPartialError: boolean;
  status: SuggestionsStatus | null;
  questionSuggestions: GuideQuestionSuggestionsBucketV2[];
  totalSuggestions: number;
  isGuideStale: boolean;
  refreshSuggestions: () => Promise<void>;
  ignoreSuggestion: (suggestionId: string) => Promise<boolean>;
  applySuggestion: (suggestionId: string) => Promise<boolean>;
  refetchSuggestions: () => Promise<void>;
};

const useGuideAssistant = (): UseGuideAssistantResult => {
  const aiInterviewer = useAiInterviewerContext();
  const isFeatureEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:suggestionsV2:frontend"
  );
  const currentVersionId = aiInterviewer.currentVersion?.id ?? null;
  const [timedOut, setTimedOut] = useState(false);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, error, startPolling, stopPolling, refetch } = useQuery<
    QueryData,
    QueryVars
  >(AI_INTERVIEWER_QUESTION_SUGGESTIONS_V2_QUERY, {
    variables: { aiInterviewerId: aiInterviewer.id, forceRefresh: false },
    skip: !isFeatureEnabled,
    fetchPolicy: "network-only",
  });

  const [ignoreMutation] = useMutation<IgnoreMutationData, IgnoreMutationVars>(
    IGNORE_GUIDE_QUESTION_SUGGESTION_V2_MUTATION
  );
  const [applyMutation] = useMutation<ApplyMutationData, ApplyMutationVars>(
    APPLY_GUIDE_QUESTION_SUGGESTION_V2_MUTATION
  );

  const status = data?.aiInterviewerQuestionSuggestionsV2?.status ?? null;
  const generatedForVersionId =
    data?.aiInterviewerQuestionSuggestionsV2?.generatedForVersionId ?? null;

  useEffect(() => {
    if (!isFeatureEnabled) {
      stopPolling();
      return;
    }

    const isPending = status === "PENDING" || status === "IN_PROGRESS";
    if (isPending && !timedOut) {
      startPolling(POLL_INTERVAL_MS);
      if (!timeoutTimerRef.current) {
        timeoutTimerRef.current = setTimeout(() => {
          setTimedOut(true);
          stopPolling();
        }, POLL_TIMEOUT_MS);
      }
      return;
    }

    stopPolling();
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, [isFeatureEnabled, status, timedOut, startPolling, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
      }
    };
  }, [stopPolling]);

  const questionSuggestions = useMemo(
    () =>
      data?.aiInterviewerQuestionSuggestionsV2?.result?.questionSuggestions ??
      [],
    [data]
  );

  const totalSuggestions = useMemo(
    () =>
      questionSuggestions.reduce(
        (acc, questionBucket) => acc + questionBucket.suggestions.length,
        0
      ),
    [questionSuggestions]
  );

  const hasPartialError = useMemo(
    () =>
      questionSuggestions.some((questionBucket) =>
        Boolean(questionBucket.analysisError)
      ),
    [questionSuggestions]
  );

  const refreshSuggestions = useCallback(async (): Promise<void> => {
    setTimedOut(false);
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    await refetch({ aiInterviewerId: aiInterviewer.id, forceRefresh: true });
    await refetch({ aiInterviewerId: aiInterviewer.id, forceRefresh: false });
  }, [aiInterviewer.id, refetch]);

  const refetchSuggestions = useCallback(async (): Promise<void> => {
    await refetch({ aiInterviewerId: aiInterviewer.id, forceRefresh: false });
  }, [aiInterviewer.id, refetch]);

  const ignoreSuggestion = useCallback(
    async (suggestionId: string): Promise<boolean> => {
      const response = await ignoreMutation({
        variables: {
          aiInterviewerId: aiInterviewer.id,
          suggestionId,
        },
      });
      return !!response.data?.ignoreGuideQuestionSuggestionV2?.success;
    },
    [aiInterviewer.id, ignoreMutation]
  );

  const applySuggestion = useCallback(
    async (suggestionId: string): Promise<boolean> => {
      const response = await applyMutation({
        variables: {
          aiInterviewerId: aiInterviewer.id,
          suggestionId,
        },
      });
      return !!response.data?.applyGuideQuestionSuggestionV2?.success;
    },
    [aiInterviewer.id, applyMutation]
  );

  const totalError =
    !!error ||
    timedOut ||
    (status === "FAILED" && (!hasPartialError || totalSuggestions === 0));

  return {
    isFeatureEnabled,
    loading:
      !timedOut &&
      (loading || status === "PENDING" || status === "IN_PROGRESS"),
    totalError,
    hasPartialError,
    status,
    questionSuggestions,
    totalSuggestions,
    // The backend only analyzes saved guide versions, so suggestions are stale
    // only after a new persisted version is created.
    isGuideStale:
      !!generatedForVersionId &&
      !!currentVersionId &&
      generatedForVersionId !== currentVersionId,
    refreshSuggestions,
    ignoreSuggestion,
    applySuggestion,
    refetchSuggestions,
  };
};

export default useGuideAssistant;
